import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Linkedin, FileDown, Heart } from "lucide-react"; // Make sure to import the new icons
import { useInView } from "react-intersection-observer";
import { AnimatedBackground } from "./AnimatedBackground";
import resumePdf from '../assets/Resume_Kenan_Smajic.pdf';

export const ContactAndFooter = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const socialLinks = [
    {
      icon: Linkedin,
      href: "https://www.linkedin.com/in/kenansmaji%C4%8719962209/",
      label: "LinkedIn",
      color: "#0077B5",
    },
    {
      icon: Mail,
      href: "mailto:ksmajic996@gmail.com",
      label: "Email",
      color: "#EA4335",
    }
  ];
  const quickLinks = ["Home", "Experience", "Skills", "Projects", "Contact"];

  return (
    <section id="contact" className="relative py-20 px-4 overflow-hidden">
      <AnimatedBackground />
      <motion.div
        ref={ref}
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
      >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8 }}
          className="text-4xl sm:text-5xl font-bold mb-16 text-center"
        >
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 relative">
            Get In Touch
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-sm" />
          </span>
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.a
            href="mailto:ksmajic996@gmail.com"
            whileHover={{ y: -5 }}
            className="flex flex-col items-center p-6 bg-gray-700/50 backdrop-blur-lg rounded-lg hover:bg-gray-700/70 transition-colors duration-300"
          >
            <Mail size={24} className="mb-4 text-blue-400" />
            <h3 className="font-semibold mb-2 text-white">Email</h3>
            <p className="text-gray-300 text-center">ksmajic996@gmail.com</p>
          </motion.a>
          <motion.a
            href="tel:+38762959069"
            whileHover={{ y: -5 }}
            className="flex flex-col items-center p-6 bg-gray-700/50 backdrop-blur-lg rounded-lg hover:bg-gray-700/70 transition-colors duration-300"
          >
            <Phone size={24} className="mb-4 text-blue-400" />
            <h3 className="font-semibold mb-2 text-white">Phone</h3>
            <p className="text-gray-300 text-center">+387 62 959 069</p>
          </motion.a>
          <motion.div
            whileHover={{ y: -5 }}
            className="flex flex-col items-center p-6 bg-gray-700/50 backdrop-blur-lg rounded-lg hover:bg-gray-700/70 transition-colors duration-300"
          >
            <MapPin size={24} className="mb-4 text-blue-400" />
            <h3 className="font-semibold mb-2 text-white">Location</h3>
            <p className="text-gray-300 text-center">Sarajevo, Bosnia and Hercegovina</p>
          </motion.div>
        </div>
      </motion.div>

      {/* Footer Section */}
      <footer className="relative mt-10 px-4  text-white overflow-hidden">
        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12 text-center md:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col items-center md:items-start"
            >
              <motion.h3
                className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"
                whileHover={{ scale: 1.05 }}
              >
                Kenan Smajić
              </motion.h3>
              <p className="text-gray-300 max-w-sm">
                Full Stack Developer crafting exceptional digital experiences with modern technologies
              </p>
            </motion.div>

            <div className="flex flex-col items-center md:items-start">
              <h4 className="text-xl font-semibold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((item) => (
                  <motion.li
                    key={item}
                    whileHover={{ x: 5 }}
                    className="text-center md:text-left"
                  >
                    <a
                      href={`#${item.toLowerCase()}`}
                      className="text-gray-300 hover:text-white transition-colors relative group"
                    >
                      {item}
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 group-hover:w-full transition-all duration-300" />
                    </a>
                  </motion.li>
                ))}
                <motion.li whileHover={{ x: 5 }}>
                  <motion.a
                    href={resumePdf}
                    download
                    className="inline-flex items-center gap-2 text-gray-300 hover:text-blue-400 transition-colors group"
                    whileHover={{ color: "#60A5FA" }}
                  >
                    <FileDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                    Download CV
                  </motion.a>
                </motion.li>
              </ul>
            </div>

            <div className="flex flex-col items-center md:items-start">
              <h4 className="text-xl font-semibold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Connect
              </h4>
              <div className="flex gap-4 flex-wrap justify-center md:justify-start">
                {socialLinks.map((item, index) => (
                  <motion.a
                    key={index}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ y: -4 }}
                    className="p-3 bg-gray-800/50 backdrop-blur-sm rounded-xl hover:bg-gray-700/50 transition-all duration-300 group relative"
                    style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)" }}
                  >
                    <item.icon className="w-5 h-5 group-hover:text-blue-400 transition-colors" />
                    <motion.span
                      className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      initial={{ y: 10 }}
                      animate={{ y: 0 }}
                    >
                      {item.label}
                    </motion.span>
                  </motion.a>
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="pt-8 mt-8 border-t border-gray-800/50 text-center"
          >
            <motion.p
              className="flex items-center justify-center gap-2"
              whileHover={{ scale: 1.05 }}
            >
              Made with{" "}
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <Heart className="w-4 h-4 text-red-500" />
              </motion.span>{" "}
              by Kenan Smajić
            </motion.p>
            <p className="mt-2 text-gray-400">
              © {new Date().getFullYear()} All rights reserved
            </p>
          </motion.div>
        </div>
      </footer>
    </section>
  );
};
